import { memo, useCallback } from 'react';
import { Switch, type SwitchProps, Typography, type TypographyProps } from '@mui/joy';
import classNames from 'classnames';

import useToggle from '@/shared/hooks/UseToggle';

import ConfirmationModal from '../../ConfirmationModal';
import { SaveIcon } from '@/shared/icons/Icons';

const CONFIRM_ON = {
  BOTH: 'both',
  CHECK: 'check',
  NONE: 'none',
  UNCHECK: 'uncheck',
} as const;

type ConfirmOn = (typeof CONFIRM_ON)[keyof typeof CONFIRM_ON];

export type SwitchFieldProps = Omit<SwitchProps, 'onChange'> & {
  confirmDescription?: React.ReactNode;
  confirmOn?: ConfirmOn;
  label: React.ReactNode;
  labelContainerProps?: Omit<TypographyProps, 'component' | 'ref' | 'startDecorator'>;
  onChange: (checked: boolean) => void;
};

const SwitchField: React.FC<SwitchFieldProps> = ({
  className,
  checked,
  confirmDescription,
  confirmOn,
  label,
  labelContainerProps,
  onChange,
  size = 'md',
  ...switchProps
}) => {
  const confirmationToggle = useToggle<boolean>();

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (
        confirmOn === CONFIRM_ON.BOTH ||
        (confirmOn === CONFIRM_ON.CHECK && e.target.checked) ||
        (confirmOn === CONFIRM_ON.UNCHECK && !e.target.checked)
      ) {
        confirmationToggle.show(e.target.checked);

        return;
      }

      onChange(e.target.checked);
    },
    [confirmOn, confirmationToggle, onChange],
  );

  const pendingChecked = confirmationToggle.state;

  const handleConfirm = useCallback(
    () => pendingChecked !== null && onChange(pendingChecked),
    [onChange, pendingChecked],
  );

  return (
    <>
      <Typography
        component="label"
        startDecorator={
          <Switch
            checked={checked}
            className={classNames('mr-2', className)}
            size={size}
            onChange={handleChange}
            {...switchProps}
          />
        }
        textColor="text.primary"
        {...labelContainerProps}
      >
        {label}
      </Typography>
      {pendingChecked !== null && (
        <ConfirmationModal
          icon={<SaveIcon />}
          toggle={confirmationToggle}
          onConfirm={handleConfirm}
        >
          {confirmDescription}
        </ConfirmationModal>
      )}
    </>
  );
};

export default memo(SwitchField);
